import { useState, useEffect } from 'react';
import { useAPI } from 'lib/API';
import {
    REMINDERS_LIMITS,
    ERROR_MESSAGES,
    DEFAULT_SETTINGS,
    SUCCESS_MESSAGES
} from 'constants/emailConfiguration';
import { renderToast, toast } from 'components/UI/ToastNotification';

import { getItems } from 'lib/SessionStorage';
import Brand, { BrandConfiguration } from 'data/Brand';
import { BusinessUnit } from 'data/BusinessUnit';
import { loadBrand, saveBrandConfiguration } from 'requests/brand';
import { STORAGE_KEYS } from 'constants/general';

interface UseEmailConfigurationFormProps {
    setIsSaveDisabled: (disabled: boolean) => void;
    setHasUnsavedChanges: (hasChanges: boolean) => void;
    selectedBusinessUnit?: BusinessUnit;
    setReadOnly?: (readOnly: boolean) => void;
}

interface UseEmailConfigurationFormReturn {
    configuration: BrandConfiguration;
    errors: Record<string, string>;
    isLoading: boolean;
    isAutoApproveDisabled: boolean;
    brandList: Brand[];
    handleRentReminderChange: (value: string) => void;
    handleFirstPaymentReminderChange: (value: string) => void;
    handleSecondPaymentReminderChange: (value: string) => void;
    handleFinalPaymentReminderChange: (value: string) => void;
    handleAutoApproveAfterFinalChange: (value: string) => void;
    handleAutoApproveChange: (checked: boolean) => void;
    handleSave: () => void;
    handleBrandChange: (value: number[]) => void;
}

export const useEmailConfigurationFormV2 = ({
    selectedBusinessUnit,
    setIsSaveDisabled,
    setHasUnsavedChanges,
    setReadOnly
}: UseEmailConfigurationFormProps): UseEmailConfigurationFormReturn => {
    const [brandConfiguration, setBrandConfiguration] = useState<BrandConfiguration>(DEFAULT_SETTINGS);
    const [errors, setErrors] = useState<Record<string, string>>({});
    const [isAutoApproveDisabled, setIsAutoApproveDisabled] = useState<boolean>(true);

    const brandReq = useAPI({ deferFn: loadBrand });

    const saveSettingsReq = useAPI({
        deferFn: saveBrandConfiguration,
        onResolve: () => {
            renderToast(toast.TYPE.SUCCESS, SUCCESS_MESSAGES.BRAND_CONFIGURATION_UPDATE);
            // Reload brand data after saving to ensure we have the latest
            if (selectedBusinessUnit?.brandId) {
                brandReq.run(selectedBusinessUnit.brandId);
            }
            setHasUnsavedChanges(false);
            setIsSaveDisabled(false);
            setReadOnly?.(true);
        },
        onReject: (err: any) => {
            renderToast(toast.TYPE.ERROR, err.message);
            setIsSaveDisabled(false);
            setReadOnly?.(true);
        }
    });

    // Reset configuration when selectedBusinessUnit changes
    useEffect(() => {
        // Clear previous brand configuration when brand changes
        setBrandConfiguration(DEFAULT_SETTINGS);
        setErrors({});

        // Only fetch if we have a valid brand ID
        if (selectedBusinessUnit?.brandId) {
            brandReq.run(selectedBusinessUnit.brandId);
        }
    }, [selectedBusinessUnit?.brandId]);

    useEffect(() => {
        if (brandReq.data && brandReq.data?.configuration) {
            // Use the brand configuration data
            setBrandConfiguration({
                ...DEFAULT_SETTINGS, // Use defaults as fallback for any missing properties
                ...brandReq.data.configuration, // Override with actual data
                brandIds: [selectedBusinessUnit?.brandId || brandReq.data.id] // Ensure brandIds is set
            });
        } else if (brandReq.data) {
            // If brand exists but no configuration, use defaults
            setBrandConfiguration({
                ...DEFAULT_SETTINGS,
                brandIds: [selectedBusinessUnit?.brandId || brandReq.data.id] // Ensure brandIds is set
            });
        }
    }, [brandReq.data]);

    // Fetching data from session storage
    const brands = getItems(STORAGE_KEYS.BRANDS);
    const [brandList, setBrandList] = useState(brands);

    useEffect(() => {
        selectedBusinessUnit ? setBrandList(brands.filter((brand: any) => brand.id === selectedBusinessUnit.brandId)) : setBrandList(brands);
    }, [selectedBusinessUnit]);

    // Helper to validate a field against min/max limits
    const validateFieldAgainstLimits = (
        field: keyof BrandConfiguration,
        limits: { MIN: number; MAX: number },
        errorsObj: Record<string, string>
    ) => {
        const value = brandConfiguration[field] as number;

        if (value !== undefined && value !== null) {
            // Check if value is 0 (not allowed)
            if (value === 0) {
                errorsObj[field] = ERROR_MESSAGES.MIN_DAYS_RANGE.replace('{MIN}', limits.MIN.toString());
            }
            // Check if value is outside the allowed range
            else if (value < limits.MIN || value > limits.MAX) {
                errorsObj[field] = ERROR_MESSAGES.DAYS_RANGE
                    .replace('{MIN}', limits.MIN.toString())
                    .replace('{MAX}', limits.MAX.toString());
            } else {
                delete errorsObj[field];
            }
        }
    };

    // Validate input and update state
    const validateNumberInput = (
        value: string,
        field: keyof BrandConfiguration,
        setDefaultValue: number | null
    ) => {
        // Parse value to number or set to default if invalid
        const numValue = parseInt(value);
        const validValue = !isNaN(numValue) ? numValue : setDefaultValue;

        // Update settings with the new value
        setBrandConfiguration({
            ...brandConfiguration,
            [field]: validValue
        });

        setHasUnsavedChanges(true);
    };

    const handleRentReminderChange = (value: string) => {
        validateNumberInput(value, 'rentReminderDays', 1);
    };

    const handleFirstPaymentReminderChange = (value: string) => {
        validateNumberInput(value, 'firstReminderDays', null);

        // Clear any sequence validation errors that might need to be rechecked
        const newErrors = { ...errors };
        delete newErrors['secondReminderDays'];
        delete newErrors['thirdReminderDays'];
        setErrors(newErrors);
    };

    const handleSecondPaymentReminderChange = (value: string) => {
        validateNumberInput(value, 'secondReminderDays', null);

        // Clear any sequence validation errors that might need to be rechecked
        const newErrors = { ...errors };
        delete newErrors['secondReminderDays'];
        delete newErrors['thirdReminderDays'];
        setErrors(newErrors);
    };

    const handleFinalPaymentReminderChange = (value: string) => {
        const numValue = parseInt(value);
        const isEmptyOrInvalid = value === '' || isNaN(numValue);

        if (isEmptyOrInvalid) {
            // If input is empty or invalid, clear the third reminder days, uncheck auto-approve and clear its days
            setBrandConfiguration({
                ...brandConfiguration,
                thirdReminderDays: undefined,
                autoApproveEnabled: false,
                autoApproveAfterDays: undefined
            });

            setIsAutoApproveDisabled(true);
        } else {
            const validValue = numValue;

            // Auto-select the auto-approve checkbox if a valid value is entered in the final reminder field
            if (validValue > 0 && validValue <= REMINDERS_LIMITS.PAYMENT.MAX) {
                setBrandConfiguration({
                    ...brandConfiguration,
                    thirdReminderDays: validValue,
                    autoApproveEnabled: true  // Auto-select the checkbox
                });
                // setIsAutoApproveDisabled(false);
            } else {
                setBrandConfiguration({
                    ...brandConfiguration,
                    thirdReminderDays: validValue
                });
            }
        }

        // Clear any sequence validation errors that might need to be rechecked
        const newErrors = { ...errors };
        delete newErrors['thirdReminderDays'];
        setErrors(newErrors);

        setHasUnsavedChanges(true);
    };

    const handleAutoApproveAfterFinalChange = (value: string) => {
        validateNumberInput(value, 'autoApproveAfterDays', null);

        const newErrors = { ...errors };
        delete newErrors['autoApproveAfterDays'];
        setErrors(newErrors);
    };

    const handleBrandChange = (value: number[]) => {
        // Update settings
        setBrandConfiguration({
            ...brandConfiguration,
            brandIds: value
        });

        setHasUnsavedChanges(true);
    }

    const handleAutoApproveChange = (checked: boolean) => {
        // If unchecking the auto-approve box, also clear the auto-approve days value
        if (!checked) {
            setBrandConfiguration({
                ...brandConfiguration,
                autoApproveEnabled: false,
                autoApproveAfterDays: undefined
            });
        } else {
            setBrandConfiguration({
                ...brandConfiguration,
                autoApproveEnabled: checked
            });
        }

        setHasUnsavedChanges(true);
    };

    // Validate that reminder days follow the correct sequence
    const validateReminderSequence = (errorsObj: Record<string, string>) => {
        const { firstReminderDays, secondReminderDays, thirdReminderDays } = brandConfiguration;

        // Only validate if both values are present (not undefined, null, or 0)
        if (firstReminderDays && secondReminderDays) {
            if (secondReminderDays <= firstReminderDays) {
                errorsObj['secondReminderDays'] = ERROR_MESSAGES.SECOND_REMINDER_SEQUENCE;
            }
        }

        if (secondReminderDays && thirdReminderDays) {
            if (thirdReminderDays <= secondReminderDays) {
                errorsObj['thirdReminderDays'] = ERROR_MESSAGES.FINAL_REMINDER_SEQUENCE;
            }
        }

        if (firstReminderDays && thirdReminderDays && !secondReminderDays) {
            if (thirdReminderDays <= firstReminderDays) {
                errorsObj['thirdReminderDays'] = ERROR_MESSAGES.FINAL_REMINDER_SEQUENCE_FIRST;
            }
        }
    };

    const handleSave = () => {
        setIsSaveDisabled(true);
        // Validate the form state before saving
        const newErrors: Record<string, string> = {};

        // Validate each field against its limits
        validateFieldAgainstLimits('rentReminderDays', REMINDERS_LIMITS.RENT, newErrors);
        validateFieldAgainstLimits('firstReminderDays', REMINDERS_LIMITS.PAYMENT, newErrors);
        validateFieldAgainstLimits('secondReminderDays', REMINDERS_LIMITS.PAYMENT, newErrors);
        validateFieldAgainstLimits('thirdReminderDays', REMINDERS_LIMITS.PAYMENT, newErrors);
        validateFieldAgainstLimits('autoApproveAfterDays', REMINDERS_LIMITS.PAYMENT, newErrors);

        // Validate the sequence of reminder days
        validateReminderSequence(newErrors);

        // Validate if there is a brand selected
        if (!brandConfiguration.brandIds || brandConfiguration.brandIds.length === 0) {
            newErrors['brands'] = ERROR_MESSAGES.BRAND_REQUIRED;
        }

        // Special validation: If final reminder is set, auto approve must be enabled
        if (brandConfiguration.thirdReminderDays && brandConfiguration.thirdReminderDays > 0) {
            if (!brandConfiguration.autoApproveEnabled) {
                newErrors['autoApproveEnabled'] = ERROR_MESSAGES.AUTO_APPROVE_REQUIRED;
            } else if (!brandConfiguration.autoApproveAfterDays || brandConfiguration.autoApproveAfterDays <= 0) {
                newErrors['autoApproveAfterDays'] = ERROR_MESSAGES.AUTO_APPROVE_DAYS_REQUIRED;
            }
        }

        setErrors(newErrors);

        if (Object.keys(newErrors).length === 0 && selectedBusinessUnit?.id) {
            saveSettingsReq.run(brandConfiguration, selectedBusinessUnit.brandId);
        } else {
            setIsSaveDisabled(false); // Enable the save button again for retry
        }
    };

    return {
        configuration:brandConfiguration,
        errors,
        isLoading: brandReq.isLoading || saveSettingsReq.isLoading,
        isAutoApproveDisabled,
        brandList,
        handleRentReminderChange,
        handleFirstPaymentReminderChange,
        handleSecondPaymentReminderChange,
        handleFinalPaymentReminderChange,
        handleAutoApproveAfterFinalChange,
        handleAutoApproveChange,
        handleSave,
        handleBrandChange
    };
};